"use client";

import type { FC } from "react";
import { memo, useCallback, useRef } from "react";
import { Box } from "@ecco/ui/box";
import { ProductImageFrame } from "@ecco/ui/product-image-frame";
import { customCursor } from "@ecco/ui/ecco-cursor";
import plus from "@ecco/ui/icons/plus.svg";
import { FIBBL_MODEL_VIEWER_CONFIG } from "./constants";
import { FibblVtoButton } from "./FibblVtoButton";
import { useFibblConfig, useFibblState } from "./FibblProvider";
import "./Fibbl.css";

type FibblContainerProps = {
    imageUrl: string;
    onImageClick: () => void;
};

export const FibblContainer: FC<FibblContainerProps> = memo(({ imageUrl, onImageClick }) => {
    const { sku } = useFibblConfig();
    const { layerRef, isVtoActive, isFibblReady, openVto, closeVto } = useFibblState();
    const qrTriggerRef = useRef<HTMLButtonElement>(null);
    const modelTriggerRef = useRef<HTMLButtonElement>(null);

    const handleOpenQR = useCallback(() => {
        qrTriggerRef.current?.click();
        openVto();
    }, [openVto]);

    const handleCloseQR = useCallback(() => {
        modelTriggerRef.current?.click();
        closeVto();
    }, [closeVto]);

    return (
        <Box position="relative" width="100%" height="100%">
            {/* Fallback product image — always visible, fibbl-layer overlays 3D on top */}
            <Box onClick={onImageClick} cursor={customCursor(plus)}>
                <ProductImageFrame
                    sizes={["100vw", "100vw", "25vw", "50vw"]}
                    src={imageUrl}
                    priority
                    alt="Product image"
                    objectFit="scale-down"
                />
            </Box>

            {/* Fibbl web component — hidden trigger buttons control which view is active */}
            <fibbl-layer ref={layerRef} data-product-id={sku}>
                <button
                    ref={modelTriggerRef}
                    data-element="fibbl-model-viewer"
                    className="fibbl-active"
                    data-ui-config={JSON.stringify(FIBBL_MODEL_VIEWER_CONFIG)}
                    //data-initial-rotation="0 180"
                    hidden
                />
                <button ref={qrTriggerRef} data-element="fibbl-qr-code" data-type="vto" hidden />
            </fibbl-layer>

            <FibblVtoButton
                isVtoActive={isVtoActive}
                isFibblReady={isFibblReady}
                onOpen={handleOpenQR}
                onClose={handleCloseQR}
            />
        </Box>
    );
});
FibblContainer.displayName = "FibblContainer";

export default FibblContainer;
